import React, { useEffect, useRef, useState } from "react";
import { confirmSetupField, generateSetupField, getSetupProposals } from "../../api/setupApi";
import "./SetupCard.css";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const CONF_LABEL = { high: "High confidence", medium: "Review", low: "Please check each" };

const POLL_MS = 8000;
const POLL_MAX = 110; // ~15 minutes

/* The API client rejects with a NORMALIZED Error (toApiError): status on
   `e.status`, the server's JSON on `e.payload`, axios' code on `e.code`. The
   raw axios shape (`e.response.*`) is only on `e.original`. Reading the raw
   shape here meant every failure — 409, 403, a 500 with a reason — collapsed
   into "Could not save. Nothing was written." Say what actually happened. */
export function explainConfirmError(e) {
  const status = Number(e?.status ?? e?.response?.status ?? e?.original?.response?.status ?? 0) || 0;
  const detail = e?.payload?.detail || e?.response?.data?.detail || "";
  const code = String(e?.code || "");
  if (status === 409) return "This changed since we proposed it. Refresh setup to review the current state.";
  if (status === 403) return detail || "Only an admin or manager can confirm setup.";
  if (status === 401) return "Your session expired — sign in again, then confirm.";
  if (status === 504 || status === 502) {
    return `The server didn't answer in time (HTTP ${status}). It may still be saving — reload Setup before trying again; if this card is gone, it saved.`;
  }
  if (!status) {
    if (code === "ECONNABORTED" || /timeout/i.test(String(e?.message || ""))) {
      return "No answer from the server after 20 seconds. It may still be saving — reload Setup before trying again; if this card is gone, it saved.";
    }
    return "Couldn't reach the server (network). Nothing was written.";
  }
  if (detail) return status >= 500 ? `${detail} (HTTP ${status})` : detail;
  return `Could not save — the server returned HTTP ${status} with no reason. Nothing was written.`;
}

export function explainGenerateError(e) {
  const status = Number(e?.status ?? e?.response?.status ?? 0) || 0;
  const detail = e?.payload?.detail || e?.response?.data?.detail || "";
  if (status === 403) return detail || "Only an admin or manager can generate setup proposals.";
  if (detail) return status >= 500 ? `${detail} (HTTP ${status})` : detail;
  if (!status) return "Couldn't reach the server to start it. Nothing was written.";
  return `Could not start that (HTTP ${status}). Nothing was written.`;
}

function elapsedLabel(startedAtIso, now) {
  const start = startedAtIso ? Date.parse(startedAtIso) : NaN;
  if (!Number.isFinite(start)) return "";
  const secs = Math.max(0, Math.round((now - start) / 1000));
  const m = Math.floor(secs / 60), s = secs % 60;
  return m ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
}

/* One proposal, reviewed and confirmed by the owner. Nothing is written until
   they press Confirm — and the value they confirm is whatever they edited it to.
   `field` drives which editor renders. On success we call onConfirmed(field). */
export default function SetupCard({ proposal, onConfirmed }) {
  const { field, label, confidence, current } = proposal;
  const [meta, setMeta] = useState({ evidence: proposal.evidence, base_fingerprint: proposal.base_fingerprint });
  const [value, setValue] = useState(() => (proposal.value == null ? null : structuredClone(proposal.value)));
  const [generating, setGenerating] = useState(!!proposal.generating);
  const [failed, setFailed] = useState(!!proposal.failed);
  const [startedAt, setStartedAt] = useState(proposal.started_at || null);
  const [now, setNow] = useState(() => Date.now());
  const [note, setNote] = useState("");
  const [edited, setEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const { evidence, base_fingerprint } = meta;
  const needsGenerate = value == null && proposal.generatable;

  /* Poll the proposals until this card comes back with a value (ready), a
     failure (failed → Retry), or disappears. Used after Generate AND on mount
     when the server says a draft is already running — a reload must not leave
     a card stuck on "Reading your pages…" with nothing watching it. */
  const pollUntilSettled = async () => {
    for (let i = 0; i < POLL_MAX; i++) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      if (!alive.current) return;
      let fresh;
      try { fresh = await getSetupProposals(); } catch { continue; }
      if (!alive.current) return;
      setNow(Date.now());
      const card = (fresh?.proposals || []).find((p) => p.field === field);
      if (!card) { setNote(""); setGenerating(false); setErr("That field is no longer offered — reload setup."); return; }
      if (card.value != null) {
        setValue(structuredClone(card.value));
        setMeta({ evidence: card.evidence, base_fingerprint: card.base_fingerprint });
        setNote(""); setGenerating(false); setFailed(false);
        return;
      }
      if (card.started_at) setStartedAt(card.started_at);
      if (!card.generating) {
        setNote(""); setGenerating(false); setFailed(true);
        setErr(card.evidence || "That didn't finish. You can try again.");
        return;
      }
    }
    if (!alive.current) return;
    setNote(""); setGenerating(false);
    setErr("Still running. It finishes on its own — reopen setup later and it'll be waiting here.");
  };

  useEffect(() => {
    if (!proposal.generating || proposal.value != null) return undefined;
    setNote("A draft is already being read from your pages — this card updates on its own.");
    pollUntilSettled();   // the elapsed-time tick runs off `generating`, which is already true here
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    setGenerating(true);
    setFailed(false);
    setErr("");
    try {
      const out = await generateSetupField(field);
      setStartedAt(out?.started_at || new Date().toISOString());
      setNote("Reading your pages — this can take a few minutes if the queue is busy.");
      await pollUntilSettled();
    } catch (e) {
      setGenerating(false);
      setErr(explainGenerateError(e));
    }
  };
  useEffect(() => {
    if (!generating) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [generating]);

  const mark = (next) => {
    setEdited(true);
    setValue(next);
  };

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const payload = buildPayload(field, value);
      await confirmSetupField({
        field,
        value: payload,
        baseFingerprint: base_fingerprint,
        source: edited ? "owner_corrected" : "inferred",
      });
      setDone(true);
      onConfirmed && onConfirmed(field);
    } catch (e) {
      setErr(explainConfirmError(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="setup-card setup-card--done">
        <div className="setup-card__title">{label}</div>
        <div className="setup-card__done">✓ Confirmed and saved.</div>
      </div>
    );
  }

  return (
    <div className="setup-card">
      <div className="setup-card__head">
        <div className="setup-card__title">{label}</div>
        <span className={`setup-badge setup-badge--${confidence}`}>{CONF_LABEL[confidence] || confidence}</span>
      </div>
      {current ? <div className="setup-card__current">Currently: {current}</div> : null}
      <p className="setup-card__evidence">{evidence}</p>

      <div className="setup-card__editor">
        {needsGenerate && (
          <div className="setup-generate">
            <button className="setup-btn setup-btn--primary" disabled={generating} onClick={generate}>
              {generating ? "Reading your pages…" : failed ? "Retry" : "Generate from your website"}
            </button>
            <span className="setup-card__hint" role="status">
              {generating
                ? `${note || "Reading your pages…"}${startedAt ? ` ${elapsedLabel(startedAt, now)} so far — checks every 8s.` : ""}`
                : note || "Nothing is saved until you review it and press Confirm."}
            </span>
          </div>
        )}
        {value != null && field === "business_hours" && <HoursEditor value={value} onChange={mark} />}
        {value != null && field === "identity" && <IdentityEditor value={value} onChange={mark} />}
        {value != null && field === "offering_service_types" && <ServiceTypeEditor value={value} onChange={mark} />}
        {value != null && !["business_hours", "identity", "offering_service_types"].includes(field) && <GenericEditor field={field} value={value} onChange={mark} />}
      </div>

      {err ? <div className="setup-card__err">{err}</div> : null}
      <div className="setup-card__actions">
        <button className="setup-btn setup-btn--primary" disabled={busy || value == null} onClick={submit}>
          {busy ? "Saving…" : "Confirm"}
        </button>
        <span className="setup-card__hint">
          {edited ? "You edited this — it'll be saved as your correction." : "Saved as-is unless you change it."}
        </span>
      </div>
    </div>
  );
}

/* ── editors ──────────────────────────────────────────────────────────────── */
function HoursEditor({ value, onChange }) {
  const set = (day, patch) => onChange({ ...value, [day]: { ...value[day], ...patch } });
  return (
    <table className="setup-hours">
      <tbody>
        {WEEKDAYS.map((d) => {
          const row = value[d] || { is_closed: true };
          return (
            <tr key={d} className={row.is_closed ? "is-closed" : ""}>
              <td className="setup-hours__day">{d}</td>
              <td>
                <label className="setup-hours__toggle">
                  <input
                    type="checkbox"
                    checked={!row.is_closed}
                    onChange={(e) =>
                      set(d, e.target.checked
                        ? { is_closed: false, open: row.open || "08:00", close: row.close || "17:00" }
                        : { is_closed: true })
                    }
                  />
                  Open
                </label>
              </td>
              <td>
                {!row.is_closed && (
                  <span className="setup-hours__times">
                    <input type="time" value={row.open || "08:00"} onChange={(e) => set(d, { open: e.target.value })} />
                    <span>to</span>
                    <input type="time" value={row.close || "17:00"} onChange={(e) => set(d, { close: e.target.value })} />
                  </span>
                )}
                {row.is_closed && <span className="setup-hours__closed">Closed</span>}
              </td>
              <td className="setup-hours__n">{row.n ? `${row.n} appts` : ""}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function IdentityEditor({ value, onChange }) {
  return (
    <div className="setup-identity">
      {"business_name" in value && (
        <label>
          Business name
          <input value={value.business_name || ""} onChange={(e) => onChange({ ...value, business_name: e.target.value })} />
        </label>
      )}
      {"owner_name" in value && (
        <label>
          Owner name
          <input value={value.owner_name || ""} onChange={(e) => onChange({ ...value, owner_name: e.target.value })} />
        </label>
      )}
    </div>
  );
}

function ServiceTypeEditor({ value, onChange }) {
  // value is a list of {offering_id, offering_name, service_type_id, service_type_name, via}
  const rows = Array.isArray(value) ? value : [];
  const [excluded, setExcluded] = useState(() => new Set());
  const toggle = (id) => {
    const next = new Set(excluded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExcluded(next);
    // reflect exclusions into the value the parent confirms
    onChange(rows.filter((r) => !next.has(r.offering_id)));
  };
  return (
    <table className="setup-stypes">
      <tbody>
        {rows.map((r) => (
          <tr key={r.offering_id} className={excluded.has(r.offering_id) ? "is-excluded" : ""}>
            <td>
              <label className="setup-stypes__inc">
                <input type="checkbox" checked={!excluded.has(r.offering_id)} onChange={() => toggle(r.offering_id)} />
              </label>
            </td>
            <td className="setup-stypes__off">{r.offering_name}</td>
            <td className="setup-stypes__arrow">→</td>
            <td className="setup-stypes__type">{r.service_type_name}</td>
            <td>
              <span className={`setup-via setup-via--${(r.via || "").startsWith("external_id") ? "exact" : "name"}`}>
                {(r.via || "").startsWith("external_id") ? "exact match" : "name guess"}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── generic editor: every field without a bespoke editor (service_prices, contact,
   service_area, scheduling_policies, faq, service_descriptions) renders its value
   so the owner SEES what they confirm — a list of strings as removable chips, a
   list of objects as editable rows (id-like keys read-only), an object as
   key/value inputs. Removing a row = "don't confirm this one". ─────────────── */
const READ_ONLY_KEYS = new Set(["service_type_id", "id", "name", "source", "via", "frequency"]);
const KEY_LABEL = { q: "Question", a: "Answer", description: "Description", ai_guidance: "Guidance for the assistant (internal)",
  initial_price: "Initial price", recurring_price: "Per visit", public_website: "Website", support_email: "Office email",
  min_lead_hours: "Minimum lead time (hours)", same_day_allowed: "Same-day bookings allowed", max_days_out: "Book up to (days out)",
  cancellation_notice_hours: "Cancellation notice (hours)", states: "States", cities: "Cities", regions: "Regions", zip_codes: "ZIP codes" };
const label = (k) => KEY_LABEL[k] || String(k).replace(/_/g, " ");
function GenericEditor({ field, value, onChange }) {
  if (value == null) return null;   // nothing to review yet — never print "null" to the owner
  if (Array.isArray(value) && value.every((v) => typeof v !== "object")) {
    return (
      <div className="setup-chips">
        {value.map((v, i) => (
          <span key={`${v}-${i}`} className="setup-chip">{String(v)}<button type="button" aria-label="remove" onClick={() => onChange(value.filter((_, j) => j !== i))}>×</button></span>
        ))}
        {!value.length && <span className="setup-card__hint">Nothing left — confirm saves an empty list.</span>}
      </div>
    );
  }
  if (Array.isArray(value)) {
    return (
      <div className="setup-rows">
        {value.map((row, i) => (
          <div key={row.service_type_id ?? row.id ?? i} className="setup-row">
            <div className="setup-row__head">
              <strong>{row.name || row.q || `#${i + 1}`}</strong>
              <button type="button" className="setup-row__remove" onClick={() => onChange(value.filter((_, j) => j !== i))}>Don’t confirm this one</button>
            </div>
            {Object.entries(row).filter(([k]) => !READ_ONLY_KEYS.has(k) && k !== "q").map(([k, v]) => (
              <label key={k} className="setup-row__field">
                <span>{label(k)}</span>
                {typeof v === "boolean"
                  ? <input type="checkbox" checked={!!v} onChange={(e) => onChange(value.map((r, j) => (j === i ? { ...r, [k]: e.target.checked } : r)))} />
                  : String(v ?? "").length > 80
                    ? <textarea rows={3} value={v ?? ""} onChange={(e) => onChange(value.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)))} />
                    : <input value={v ?? ""} onChange={(e) => onChange(value.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)))} />}
              </label>
            ))}
            {row.source && <a className="setup-row__source" href={row.source} target="_blank" rel="noreferrer">source</a>}
          </div>
        ))}
        {!value.length && <span className="setup-card__hint">Nothing left to confirm.</span>}
      </div>
    );
  }
  if (value && typeof value === "object") {
    return (
      <div className="setup-kv">
        {Object.entries(value).map(([k, v]) => (
          <label key={k} className="setup-row__field">
            <span>{label(k)}</span>
            {Array.isArray(v)
              ? <GenericEditor field={field} value={v} onChange={(nv) => onChange({ ...value, [k]: nv })} />
              : typeof v === "boolean"
                ? <input type="checkbox" checked={!!v} onChange={(e) => onChange({ ...value, [k]: e.target.checked })} />
                : <input value={v ?? ""} onChange={(e) => onChange({ ...value, [k]: typeof v === "number" ? Number(e.target.value) : e.target.value })} />}
          </label>
        ))}
      </div>
    );
  }
  return <pre className="setup-card__hint">{JSON.stringify(value, null, 2)}</pre>;
}

/* ── payload shaping (what the confirm endpoint expects per field) ─────────── */
function buildPayload(field, value) {
  if (field === "offering_service_types") {
    const rows = Array.isArray(value) ? value : [];
    return rows.map((r) => ({ offering_id: r.offering_id, service_type_id: r.service_type_id }));
  }
  // business_hours + identity are sent as-is (already the right shape)
  return value;
}
